import { Component, OnInit, ViewChild, Input, HostListener } from '@angular/core';
import { BaseModal } from 'src/app/core/utils/BaseModal';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import {
  ModalService,
  StorageService,
  StorageKey,
  UserService,
  AlertService,
  BrandModelService,
  ColorService,
  CategoryService,
  ClassificationService,
  TypeService,
  RegionService,
  PreProcessSettingsService
} from 'src/app/core/services';
import {
  User,
  Violation,
  AlertItem,
  AlertType,
  Vehicle,
  BrandModel,
  Color,
  Category,
  Classification,
  Type,
  Region,
  PreProcessSettings
} from 'src/app/core/models';
import { Subject } from 'rxjs';
import { ViolationService } from 'src/app/core/services/violation.service';
import { PlateService } from 'src/app/core/services/plate.service';
import { BrandInternal } from 'src/app/core/models/BrandInternal';
import { City } from 'src/app/core/models/City';
import { State } from 'src/app/core/models/State';
import { BrandInternalService } from 'src/app/core/services/brandInternal.service';
import { CityService } from 'src/app/core/services/city.service';
import { StateService } from 'src/app/core/services/state.service';

@Component({
  selector: 'app-search-plate-panel',
  templateUrl: './search-plate-panel.component.html',
  styleUrls: ['./search-plate-panel.component.sass']
})
export class SearchPlatePanelComponent extends BaseModal implements OnInit {
  @Input() violation: Violation;
  public form: FormGroup;
  params: { contractId: string; plate: string; regulationId: string; date: Date };

  public isLoading: boolean;
  public plateRegex: string;
  public plateSearchPromise = null;
  public vehicleData: Vehicle;
  public regions: Array<Region>;
  public brandModels: Array<BrandModel>;
  public colors: Array<Color>;
  public categories: Array<Category>;
  public classifications: Array<Classification>;
  public types: Array<Type>;
  public brandInternalModels: Array<BrandInternal>;
  public cities: Array<City>;
  public states: Array<State>;
  public viewData: boolean;

  public brandModelsById: { [key: string]: BrandModel } = {};
  public colorById: { [key: string]: Color } = {};
  public categoryById: { [key: string]: Category } = {};
  public classificationById: { [key: string]: Classification } = {};
  public typeById: { [key: string]: Type } = {};
  public brandInternalModelsById: { [key: string]: BrandInternal } = {};
  public cityById: { [key: string]: City } = {};
  public stateById: { [key: string]: State } = {};
  public preProcessSettings: PreProcessSettings;

  get isValid() {
    return this.form.valid || this.viewData;
  }

  constructor(
    modalService: ModalService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private plateService: PlateService,
    private regionService: RegionService,
    private brandModelService: BrandModelService,
    private colorService: ColorService,
    private categoryService: CategoryService,
    private classificationService: ClassificationService,
    private typeService: TypeService,
    private brandInternalService: BrandInternalService,
    private cityService: CityService,
    private stateService: StateService,
    private preProcessService: PreProcessSettingsService
  ) {
    super(modalService);
  }

  async ngOnInit() {
    this.viewData = false;
    this.violation = this.initialState as Violation || new Violation();
    this.preProcessService.getById(this.violation.contractId).then(preProcessSettings => {
      this.preProcessSettings = preProcessSettings;
    });

    this.createForm();
    await this.setRegions();
  }

  async setRegions() {
    await this.regionService.getAll().then(regions => {
      this.regions = regions;
      this.plateRegex = regions[0].plateRegex;
    });
    this.createForm();
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      plate: ['', [Validators.pattern(this.plateRegex)]]
    });
    this.form.get('plate').valueChanges.subscribe(plate => {
      if (plate.length === 7) {
        this.params = {
          contractId: this.violation?.contractId,
          regulationId: this.violation?.regulationId,
          date: this.violation?.date,
          plate: `${plate.toUpperCase()}`
        };
        if (this.isLoading !== false) {
          this.isLoading = true;
        }
        this.plateSearchPromise = this.findPlate(this.params);
      } else {
        this.viewData = false;
      }
    });
  }

  async findPlate(params: { contractId: string; plate: string; regulationId: string; date: Date }) {
    return await this.plateService.searchPlate(this.params).then(async res => {
      this.vehicleData = res.vehicle;
      if (this.vehicleData && !this.vehicleData.uf && (res.vehicle as any)?.abbreviation) {
        this.vehicleData.uf = (res.vehicle as any).abbreviation;
      }
      if (!this.vehicleData?.brandModel) {
        await this.getAllData();
      }
      this.viewData = true;
      this.isLoading = false;
    }).catch(err => {
      if (this.vehicleData === null && this.getValue(this.preProcessSettings, 'allowInputNotFoundVehicle')) {
        this.alertService.show(new AlertItem('PlateNotFoundAuthorized', AlertType.warning, false, 5000));
      }
      if (
        this.vehicleData?.plate === '' && !this.getValue(this.preProcessSettings, 'allowInputNotFoundVehicle') ||
        this.vehicleData === null && !this.getValue(this.preProcessSettings, 'allowInputNotFoundVehicle')
      ) {
        this.alertService.show(new AlertItem('PlateNotFoundNotAuthorized', AlertType.warning, false, 3000));
      } else if (this.vehicleData?.plate === '' && this.getValue(this.preProcessSettings, 'allowInputNotFoundVehicle')) {
        this.alertService.show(new AlertItem('PlateNotFoundAuthorized', AlertType.warning, false, 5000));
      }
      this.viewData = false;
      this.isLoading = false;
    });
  }

  async getAllData() {
    await this.brandModelService.getById(this.vehicleData.brandModelId).then(brand => {
      this.brandModelsById[brand.id] = brand;
    });
    if (this.preProcessSettings?.changeBrandModelForClientBrand === true) {
      await this.brandInternalService.getById(this.vehicleData.brandModelId).then(brandInternal => {
        this.brandInternalModelsById[brandInternal.id] = brandInternal;
      });
    }
    await this.colorService.getById(this.vehicleData.colorId).then(color => {
      this.colorById[color.id] = color;
    });
    await this.categoryService.getById(this.vehicleData.categoryId).then(category => {
      this.categoryById[category.id] = category;
    });
    await this.classificationService.getById(this.vehicleData.classificationId).then(classification => {
      this.classificationById[classification.id] = classification;
    });
    await this.typeService.getById(this.vehicleData.typeId).then(type => {
      this.typeById[type.id] = type;
    });
    await this.cityService.getById(this.vehicleData.cityId).then(city => {
      this.cityById[city.id] = city;
    });
    await this.stateService.getById(this.cityById[this.vehicleData.cityId]?.stateId).then(state => {
      this.stateById[state.id] = state;
    });
    this.isLoading = false;
  }

  getValue(model, attr, value = '') {
    let arr = [];
    if (typeof attr === 'string') {
      arr = attr.split('.');
    } else {
      arr = attr;
    }
    if (arr.length > 0) {
      if (model[arr[0]] == null) {
        return value;
      } else {
        return this.getValue(model[arr[0]], arr.slice(1), value);
      }
    } else {
      return model;
    }
  }

  onAllSubmited(): Promise<any> {
    return Promise.resolve(this.vehicleData);
  }

  get lastState(): any {
    return null;
  }
}
